import {
    authPillButtonClass,
    authSecondaryButtonClass,
    Button,
    Icons,
    Input,
    Label,
} from '@longy/web-components'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { AuthLayout } from '../components/auth/AuthLayout'
import { api, getTRPCErrorMessage } from '../core/trpc'
import { useAuth } from '../hooks/useAuth'
import { getJwt, removeJwt } from '../utils/jwt'
import { safeRedirectTarget } from '../utils/redirect'

/**
 * The route id carries the trailing underscore, the path does not: `login_.two-factor` is what
 * un-nests this from `login.tsx`. Named `login.two-factor` it became a child of the login route,
 * which renders no Outlet — so navigating here re-rendered the login form and the code was never
 * asked for.
 */
const PATH = '/login_/two-factor'
const RESEND_COOLDOWN_SECONDS = 4 * 60

const twoFactorSearchSchema = z.object({
    redirect: z.string().optional(),
})

const formatCountdown = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`

const TwoFactorPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const auth = useAuth()
    const { redirect } = Route.useSearch()

    const [code, setCode] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS)
    const verified = useRef(false)

    const { mutateAsync: sendCode } = api.user.sendLoginTwoFactorCode.useMutation()
    const { mutateAsync: verifyCode, isPending } = api.user.verifyLoginTwoFactorCode.useMutation()

    useEffect(() => {
        const jwt = getJwt()

        // Reached without the half-authenticated token the login form stores — a direct visit or a
        // reload after it was cleared. There is nothing to verify against, so start over.
        if (!jwt) {
            navigate({ to: '/login', search: { redirect } })
            return
        }

        sendCode({ resend: false }).catch(() => setError(t('common.errors.defaultError')))

        return () => {
            // Leaving without finishing must not leave a usable session behind: the token is only
            // held so the two authenticated 2FA procedures can be called.
            if (!verified.current) removeJwt()
        }
    }, [])

    useEffect(() => {
        if (countdown <= 0) return

        const timer = setInterval(() => setCountdown((previous) => previous - 1), 1000)
        return () => clearInterval(timer)
    }, [countdown])

    const onVerify = async () => {
        setError(null)

        try {
            await verifyCode({ code })
            verified.current = true

            const jwt = getJwt()
            if (jwt) await auth.login(jwt)

            await navigate({ to: safeRedirectTarget(redirect) ?? '/' })
        } catch (caught) {
            setError(getTRPCErrorMessage(caught))
        }
    }

    const onResend = async () => {
        setError(null)

        try {
            await sendCode({ resend: true })
            setCountdown(RESEND_COOLDOWN_SECONDS)
            setCode('')
        } catch (caught) {
            setError(getTRPCErrorMessage(caught))
        }
    }

    return (
        <AuthLayout
            title={t('frontend.auth.twoFactorTitle')}
            subtitle={t('frontend.auth.twoFactorHint')}
        >
            <div className="flex flex-col gap-2">
                <Label htmlFor="two-factor-code">{t('frontend.auth.twoFactorCode')}</Label>
                <Input
                    id="two-factor-code"
                    className="h-12 text-center text-lg tracking-widest"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    disabled={isPending}
                    onChange={(event) => setCode(event.target.value)}
                />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button
                className={authPillButtonClass}
                disabled={isPending || code.length === 0}
                onClick={onVerify}
            >
                {isPending && <Icons.Spinner className="mr-2 h-4 animate-spin" />}
                {t('frontend.auth.verify')}
            </Button>

            <Button
                className={authSecondaryButtonClass}
                variant="ghost"
                disabled={countdown > 0}
                onClick={onResend}
            >
                {countdown > 0
                    ? `${t('frontend.auth.resendCode')} (${formatCountdown(countdown)})`
                    : t('frontend.auth.resendCode')}
            </Button>
        </AuthLayout>
    )
}

export const Route = createFileRoute(PATH)({
    ssr: false,
    validateSearch: twoFactorSearchSchema,
    head: () => ({ meta: [{ title: 'Long-Y - Kétlépcsős azonosítás' }] }),
    component: TwoFactorPage,
})
